// Environment Configuration for SaaS Deployment
// This file centralizes all environment-specific settings for different deployment scenarios

export interface EnvironmentConfig {
  // API Configuration
  apiBaseUrl: string;
  environment: 'local' | 'demo' | 'development' | 'staging' | 'production';
  
  // Authentication Settings
  auth: {
    enabled: boolean;
    provider: 'azure' | 'demo' | 'mock';
    azure?: {
      clientId: string;
      tenantId: string;
      redirectUri: string;
    };
  };
  
  // Feature Flags
  features: {
    chatbot: boolean;
    audioPlayback: boolean;
    fileUpload: boolean;
    settings: boolean;
    analytics: boolean;
  };
  
  // Security Settings
  security: {
    enforceHttps: boolean;
    corsOrigins: string[];
    maxFileSize: number; // bytes
  };
  
  // Logging & Monitoring
  logging: {
    level: 'debug' | 'info' | 'warn' | 'error';
    enableApiLogging: boolean;
    enablePerformanceMonitoring: boolean;
  };
  
  // External Services
  external: {
    contactUrl: string;
    supportEmail: string;
    documentationUrl: string;
  };
  
  // Demo-specific Settings
  demo?: {
    restrictedFeatures: string[];
    contactRedirectUrl: string;
    demoUsers: Array<{
      email: string;
      password: string;
      role: 'Admin' | 'Demo';
      displayName: string;
    }>;
  };
}

// Local Development Environment
export const localConfig: EnvironmentConfig = {
  apiBaseUrl: 'http://localhost:5000',
  environment: 'local',
  
  auth: {
    enabled: false,
    provider: 'mock',
  },
  
  features: {
    chatbot: true,
    audioPlayback: true,
    fileUpload: true,
    settings: true,
    analytics: true,
  },
  
  security: {
    enforceHttps: false,
    corsOrigins: ['http://localhost:3000', 'http://localhost:5173'],
    maxFileSize: 100 * 1024 * 1024, // 100MB
  },
  
  logging: {
    level: 'debug',
    enableApiLogging: true,
    enablePerformanceMonitoring: false,
  },
  
  external: {
    contactUrl: 'https://goviq.us/#contact',
    supportEmail: 'contact@goviq.us',
    documentationUrl: 'https://docs.goviq.us',
  },
};

// Demo Environment (Public Safety Demos)
export const demoConfig: EnvironmentConfig = {
  apiBaseUrl: process.env.VITE_API_BASE_URL || 'https://demo-api.goviq.us',
  environment: 'demo',
  
  auth: {
    enabled: true,
    provider: 'demo',
  },
  
  features: {
    chatbot: true,
    audioPlayback: false, // Restricted for demo users
    fileUpload: false,    // Restricted for demo users
    settings: false,      // Restricted for demo users
    analytics: true,
  },
  
  security: {
    enforceHttps: true,
    corsOrigins: ['https://demo.goviq.us', 'https://a3demo.azurewebsites.net'],
    maxFileSize: 50 * 1024 * 1024, // 50MB
  },
  
  logging: {
    level: 'info',
    enableApiLogging: false,
    enablePerformanceMonitoring: true,
  },
  
  external: {
    contactUrl: 'https://goviq.us/#contact',
    supportEmail: 'demo@goviq.us',
    documentationUrl: 'https://docs.goviq.us/demo',
  },
  
  demo: {
    restrictedFeatures: ['audioPlayback', 'fileUpload', 'settings'],
    contactRedirectUrl: 'https://goviq.us/#contact?demo=true',
    demoUsers: [
      {
        email: 'demo@a3.demo.com',
        password: 'a3-demo-user',
        role: 'Demo',
        displayName: 'Demo User',
      },
      {
        email: 'admin@a3.demo.com',
        password: 'a3-admin-user',
        role: 'Admin',
        displayName: 'Admin User',
      },
    ],
  },
};

// Development Environment
export const developmentConfig: EnvironmentConfig = {
  apiBaseUrl: process.env.VITE_API_BASE_URL || 'https://dev-api.goviq.us',
  environment: 'development',
  
  auth: {
    enabled: true,
    provider: 'azure',
    azure: {
      clientId: process.env.VITE_AZURE_CLIENT_ID || '',
      tenantId: process.env.VITE_AZURE_TENANT_ID || '',
      redirectUri: process.env.VITE_AZURE_REDIRECT_URI || 'https://dev.goviq.us/auth/callback',
    },
  },
  
  features: {
    chatbot: true,
    audioPlayback: true,
    fileUpload: true,
    settings: true,
    analytics: true,
  },
  
  security: {
    enforceHttps: true,
    corsOrigins: ['https://dev.goviq.us'],
    maxFileSize: 100 * 1024 * 1024, // 100MB
  },
  
  logging: {
    level: 'debug',
    enableApiLogging: true,
    enablePerformanceMonitoring: true,
  },
  
  external: {
    contactUrl: 'https://goviq.us/#contact',
    supportEmail: 'contact@goviq.us',
    documentationUrl: 'https://docs.goviq.us',
  },
};

// Staging Environment
export const stagingConfig: EnvironmentConfig = {
  apiBaseUrl: process.env.VITE_API_BASE_URL || 'https://staging-api.goviq.us',
  environment: 'staging',
  
  auth: {
    enabled: true,
    provider: 'azure',
    azure: {
      clientId: process.env.VITE_AZURE_CLIENT_ID || '',
      tenantId: process.env.VITE_AZURE_TENANT_ID || '',
      redirectUri: process.env.VITE_AZURE_REDIRECT_URI || 'https://staging.goviq.us/auth/callback',
    },
  },
  
  features: {
    chatbot: true,
    audioPlayback: true,
    fileUpload: true,
    settings: true,
    analytics: true,
  },
  
  security: {
    enforceHttps: true,
    corsOrigins: ['https://staging.goviq.us'],
    maxFileSize: 100 * 1024 * 1024, // 100MB
  },
  
  logging: {
    level: 'info',
    enableApiLogging: true,
    enablePerformanceMonitoring: true,
  },
  
  external: {
    contactUrl: 'https://goviq.us/#contact',
    supportEmail: 'contact@goviq.us',
    documentationUrl: 'https://docs.goviq.us',
  },
};

// Production Environment
export const productionConfig: EnvironmentConfig = {
  apiBaseUrl: process.env.VITE_API_BASE_URL || 'https://api.goviq.us',
  environment: 'production',
  
  auth: {
    enabled: true,
    provider: 'azure',
    azure: {
      clientId: process.env.VITE_AZURE_CLIENT_ID || '',
      tenantId: process.env.VITE_AZURE_TENANT_ID || '',
      redirectUri: process.env.VITE_AZURE_REDIRECT_URI || 'https://app.goviq.us/auth/callback',
    },
  },
  
  features: {
    chatbot: true,
    audioPlayback: true,
    fileUpload: true,
    settings: true,
    analytics: true,
  },
  
  security: {
    enforceHttps: true,
    corsOrigins: ['https://app.goviq.us', 'https://goviq.us'],
    maxFileSize: 200 * 1024 * 1024, // 200MB
  },
  
  logging: {
    level: 'warn',
    enableApiLogging: false,
    enablePerformanceMonitoring: true,
  },
  
  external: {
    contactUrl: 'https://goviq.us/#contact',
    supportEmail: 'contact@goviq.us',
    documentationUrl: 'https://docs.goviq.us',
  },
};

// Configuration Resolver
export function getEnvironmentConfig(): EnvironmentConfig {
  const mode = import.meta.env.MODE || 'local';
  
  switch (mode) {
    case 'demo':
      return demoConfig;
    case 'development':
      return developmentConfig;
    case 'staging':
      return stagingConfig;
    case 'production':
      return productionConfig;
    case 'local':
    default:
      return localConfig;
  }
}

// Export current configuration
export const config = getEnvironmentConfig();